import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import {
  createPasswordFieldValidator,
  createPasswordRulesTemplate,
} from '@app/finxone-web-frontend/app/lib/utils/password-field.utils';
import { compileTemplateWithCustomHandlebar } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { PasswordValidationRules } from '@app/finxone-web-frontend/app/Modules/shared/widgets/form-widgets/form-submitter/form-submitter.component';
import { Store } from '@ngxs/store';
import { AutoDestroy } from 'libs/shared/base-types/src/lib/autodestroy';
import { catchError, map, Subject, takeUntil } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-password-rules',
  templateUrl: './password-rules.component.html',
  styleUrl: './password-rules.component.css',
})
export class PasswordRulesComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Output() public allPasswordRulesPassed = new EventEmitter<boolean>();
  @Input() public passwordForm: FormGroup;
  @Input() public passwordFieldName = 'newPassword';
  @Input() public confirmPasswordFieldName = 'confirmPassword';
  @Input() public passwordMatchTemplate: string;
  @Input() public passwordNoMatchTemplate: string;
  public passwordRules: any;
  public passwordFieldValidators: PasswordValidationRules;
  public passwordRulesMessages: { [key: string]: string } = {};

  public matchTemplates: { [key: string]: string } = {};
  public noMatchTemplates: { [key: string]: string } = {};

  constructor(private configService: ConfigService, public store: Store) {}

  ngOnInit(): void {
    this.fetchPasswordRules();
  }

  public get keysOfPasswordRules() {
    return Object.keys(this.passwordRulesMessages);
  }

  private fetchPasswordRules() {
    this.configService
      .fetchPasswordRules()
      .pipe(
        map((rules) => {
          this.passwordRules = rules.policy;
          this.setValidatorsFromPasswordRules();
        }),
        catchError((error) => {
          this.passwordRules = '';
          throw new Error('failed to fetch password rules', error);
        }),
      )
      .subscribe();
  }

  private setValidatorsFromPasswordRules() {
    this.passwordFieldValidators = this.parsePasswordPolicy(this.passwordRules);
    if (this.passwordFieldValidators?.maxLength) {
      this.passwordForm
        .get(this.passwordFieldName)
        ?.addValidators(Validators.maxLength(this.passwordFieldValidators.maxLength));
      this.passwordForm
        .get(this.confirmPasswordFieldName)
        ?.addValidators(Validators.maxLength(this.passwordFieldValidators.maxLength));
    }
    if (this.passwordFieldValidators?.length) {
      this.passwordForm
        .get(this.passwordFieldName)
        ?.addValidators(Validators.minLength(this.passwordFieldValidators.length));
      this.passwordForm
        .get(this.confirmPasswordFieldName)
        ?.addValidators(Validators.minLength(this.passwordFieldValidators.length));
    }
    if (
      this.passwordFieldValidators?.digits ||
      this.passwordFieldValidators?.lowerCase ||
      this.passwordFieldValidators?.specialChars ||
      this.passwordFieldValidators?.upperCase
    ) {
      this.passwordForm
        .get(this.passwordFieldName)
        ?.addValidators(createPasswordFieldValidator(this.passwordFieldValidators));
      this.passwordForm
        .get(this.confirmPasswordFieldName)
        ?.addValidators(createPasswordFieldValidator(this.passwordFieldValidators));
    }

    this.subscribeToPasswordChanges();
  }

  public parsePasswordPolicy(policyString: string): PasswordValidationRules {
    const passwordValidationRules: PasswordValidationRules = {};
    const ruleRegex = /(\w+)(\((\d+)\))?/g;
    let match;
    while ((match = ruleRegex.exec(policyString)) !== null) {
      passwordValidationRules[match[1] as keyof PasswordValidationRules] = match[3]
        ? parseInt(match[3], 10)
        : undefined;
    }
    this.passwordRulesMessages = createPasswordRulesTemplate(passwordValidationRules);

    // prepare and cache raw templates so template bindings don't recreate them every CD
    this.prepareTemplates();

    return passwordValidationRules;
  }

  private prepareTemplates(placeholder = 'rule') {
    this.matchTemplates = {};
    this.noMatchTemplates = {};
    for (const key of Object.keys(this.passwordRulesMessages)) {
      const message = this.passwordRulesMessages[key] ?? '';
      const matchRaw = compileTemplateWithCustomHandlebar(this.passwordMatchTemplate, placeholder, message);
      const noMatchRaw = compileTemplateWithCustomHandlebar(
        this.passwordNoMatchTemplate,
        placeholder,
        message,
      );
      this.matchTemplates[key] = matchRaw;
      this.noMatchTemplates[key] = noMatchRaw;
    }
  }

  public isRulePassed(passwordRule: string): boolean {
    return this.validateFieldForSuccessHighlighting(passwordRule);
  }

  public trackByRule(_: number, item: string) {
    return item;
  }

  public validateFieldForSuccessHighlighting(passwordRule: string): boolean {
    const control = this.passwordForm.get(this.passwordFieldName);
    const value = control?.value ?? '';
    const len = value?.length ?? 0;
    switch (passwordRule) {
      case 'minLength':
        return !control?.hasError(passwordRule) && len >= (this.passwordFieldValidators?.length ?? 0);
      case 'maxLength':
        return (
          !control?.hasError(passwordRule) &&
          len >= (this.passwordFieldValidators?.length ?? 8) &&
          len <= (this.passwordFieldValidators?.maxLength ?? 8)
        );
      default:
        return !control?.hasError(passwordRule) && len !== 0;
    }
  }

  // Return cached raw HTML strings (cheap to call from template)
  public getMatchTemplate(passwordRule: string): string {
    return this.matchTemplates[passwordRule] ?? '';
  }

  public getNoMatchTemplate(passwordRule: string): string {
    return this.noMatchTemplates[passwordRule] ?? '';
  }

  private subscribeToPasswordChanges(): void {
    [this.passwordFieldName, this.confirmPasswordFieldName].forEach((fieldName) => {
      this.passwordForm
        .get(fieldName)
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.checkAndEmitPasswordRulesValidity();
        });
    });
  }

  private checkAndEmitPasswordRulesValidity(): void {
    const allPassed = this.keysOfPasswordRules.every((rule) => this.isRulePassed(rule));
    this.allPasswordRulesPassed.emit(allPassed);
  }
}
